import { timeRangeToAggregation } from '@fullstory/dqm-ui';
import { intl } from '@fullstory/intl';
import {
  GetPageDetailViewResponse,
  PageDetail,
  VolumeTimeRange,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  AppBackendRequestActions,
  AppDispatch,
  fullstorySelectors,
  pageDetailView,
  PagesRequestActions,
} from '@fullstory/redux';
import jstz from 'jstz';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface PageDetailViewParams {
  pageId: string;
  timeRange: VolumeTimeRange;
  duplicate?: boolean;
  isSuggestion?: boolean;
}

interface UsePageDetailViewReturn {
  data: GetPageDetailViewResponse | null;
  loading: boolean;
}

const duplicateDetails = (data: GetPageDetailViewResponse): GetPageDetailViewResponse | null => {
  if (!data.details?.page) {
    return null;
  }
  return {
    ...data,
    details: {
      ...data.details,
      page: {
        ...data.details.page,
        id: 0,
        friendlyId: '',
        name: intl.formatMessage(
          {
            defaultMessage: '{pageName} (copy)',
            id: '2cZA2e',
            description: 'This is the default title of a duplicated row in a table',
          },
          { pageName: data.details!.page!.name },
        ),
        isUserDefined: true,
        archived: false,
        createdAt: null,
        createdBy: '',
        updatedAt: null,
        updatedBy: '',
      },
    },
  };
};

const suggestionToEditableDetails = (suggestion: PageDetail): GetPageDetailViewResponse | null => {
  if (!suggestion.page) {
    return null;
  }

  return {
    details: {
      silence: null,
      page: {
        ...suggestion.page,
      },
    },
    objectReferences: null,
  };
};

export const usePageDetailView = ({
  pageId,
  timeRange,
  duplicate,
  isSuggestion,
}: PageDetailViewParams): UsePageDetailViewReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const requestData = useSelector(pageDetailView);
  const timezone = jstz.determine().name();
  const [details, setDetails] = useState<GetPageDetailViewResponse | null>(null);

  const loadSuggestedPageDetails = useCallback(
    (suggestionId: string) => {
      const req = PagesRequestActions.getSuggestedPage({
        orgId,
        suggestionId,
      });

      dispatch(req).then(data => {
        const suggestion = data.suggestion;
        if (suggestion) {
          const details = suggestionToEditableDetails(suggestion);
          setDetails(details);
        }
      });
    },
    [orgId, dispatch],
  );

  const loadDetails = useCallback(
    (pageId: string, duplicate?: boolean) => {
      if (pageId !== '0') {
        const req = AppBackendRequestActions.getPageDetailView({
          orgId,
          pageId,
          timeRange,
          timezone,
          volumeAggregateBy: timeRangeToAggregation[timeRange],
        });
        dispatch(req).then(data => {
          const d = duplicate ? duplicateDetails(data) : data;
          setDetails(d);
        });
      }
    },
    [orgId, timeRange, timezone, dispatch],
  );

  useEffect(() => {
    if (isSuggestion) {
      loadSuggestedPageDetails(pageId);
    } else {
      loadDetails(pageId, duplicate);
    }
  }, [
    dispatch,
    duplicate,
    isSuggestion,
    loadDetails,
    loadSuggestedPageDetails,
    orgId,
    pageId,
    timeRange,
    timezone,
  ]);

  return {
    loading: requestData.loading,
    data: details,
  };
};
